import { useCallback, useEffect, useState } from 'react'
import { Script } from '@calendly/react-script-tag'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'astroturf/react'
import { SafeSuspense } from '@calendly/safe-suspense'
const fontWeightSemibold = 'var(--fontWeightSemibold, 600)'

import {
  EnterDetailsFieldStackNested,
  EnterDetailsFieldset
} from 'components/enter_details/form_field_spacing'
import Info from 'components/enter_details/payment/info'
import { StripeForm } from 'components/enter_details/payment/stripe_form'
import StripeContainer from 'components/payment/stripe/container'
import PaypalForm from 'components/enter_details/payment/paypal_form'
import ErrorMessage from 'components/form/error_message'
import { Settings } from 'services/settings'
import { formFieldUpdated } from 'actions/scheduling'
import { eventTypeSelector, useValidateCouponMutation } from 'root-api'
import { useI18n } from 'locales'
import Coupon from 'components/enter_details/payment/coupon'
import InfoCoupon from 'components/enter_details/payment/info_coupon'
import { stripeClientSecretSelector } from 'selectors/payment'

import { parseAmountFromSymbol, calculateFinalPaymentAmount } from './booking_code_utils'
import { buildLegacyPaypalSdkQuery, buildPpcpPaypalSdkQuery } from './paypal_sdk_query'
import _Information from "index-Information.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/enter_details/payment/index.js?styleId=Information";


const Information = /*#__PURE__*/styled('div', null, {
  displayName: "Information",
  styles: _Information
})

const PaymentForm = ({ registerApi, onPaypalAuthorized }) => {
  const { i18n } = useI18n()
  const {
    payment_method: { provider, provider_locale: providerLocale, currency } = {},
    payment_account: { external_user_id: externalUserId } = {},
    paypal_ppcp_feature_enabled: paypalPpcpFeatureEnabled
  } = useSelector(eventTypeSelector)

  // `paypal_ppcp` from Meetings is only for the PPCP integration; do not render legacy PayPal for it.
  // When the booking-side PPCP flag is off, omit PayPal entirely (no SDK / no buttons).
  const ppcpFlagOn = Boolean(paypalPpcpFeatureEnabled)
  const usePpcpCheckout = provider === 'paypal_ppcp' && ppcpFlagOn
  const useLegacyPaypalCheckout = provider === 'paypal'

  const renderTranslatedErrorMessage = useCallback(() => (
    <ErrorMessage message={i18n.t('errors.billing.card.payment_form_not_loaded')} />
  ), [i18n])

  const eventScheduled = useSelector((state) => Object.keys(state.scheduling.event).length !== 0)

  // Prevent form re-rendering when an event has been scheduled
  if (eventScheduled) return null

  return (
    <SafeSuspense onError={renderTranslatedErrorMessage}>
      {provider === 'stripe' && (
        <StripeContainer stripeAccount={externalUserId}>
          <StripeForm registerApi={registerApi} />
        </StripeContainer>
      )}
      {useLegacyPaypalCheckout && (
        <Script
          src={`https://www.paypal.com/sdk/js?${buildLegacyPaypalSdkQuery(externalUserId, providerLocale, currency)}`}
          data-partner-attribution-id="Calendly_SP"
        >
          <PaypalForm
            registerApi={registerApi}
            ordersPath="/api/booking/paypal_orders"
            paypalEnv={Settings.paypal.env}
            paypalClientId={Settings.paypal.client_id}
            isPpcp={false}
            onPaypalAuthorized={onPaypalAuthorized}
          />
        </Script>
      )}
      {usePpcpCheckout && (
        <Script
          src={`https://www.paypal.com/sdk/js?${buildPpcpPaypalSdkQuery(externalUserId, providerLocale, currency)}`}
          data-partner-attribution-id={Settings.paypal_ppcp.partner_attribution_id}
        >
          <PaypalForm
            registerApi={registerApi}
            ordersPath="/api/booking/paypal_ppcp_orders"
            paypalEnv={Settings.paypal_ppcp.env}
            paypalClientId={Settings.paypal_ppcp.client_id}
            isPpcp
            onPaypalAuthorized={onPaypalAuthorized}
          />
        </Script>
      )}
    </SafeSuspense>
  )
}

const Payment = ({ registerApi, inviteeEmail, onPaypalAuthorized }) => {
  const eventType = useSelector(eventTypeSelector)
  const hasActiveBookingCodes = eventType.has_active_booking_codes
  const isStripePayment = eventType.payment_method?.provider === 'stripe'
  const [showCouponInput, setShowCouponInput] = useState(true)
  const [paymentElementKey, setPaymentElementKey] = useState(0)
  const stripeClientSecret = useSelector(stripeClientSecretSelector)
  const { i18n } = useI18n()
  const {
    payment_method: {
      amount_with_symbol: amountWithSymbol, currency, payment_terms: paymentTerms
    } = {},
    uuid: eventTypeUuid
  } = eventType

  const [
    validateCoupon,
    { data: couponData }
  ] = useValidateCouponMutation({
    fixedCacheKey: 'couponData'
  })

  const dispatch = useDispatch()

  const finalPaymentResult = calculateFinalPaymentAmount(amountWithSymbol, couponData)
  const finalPaymentAmount = parseAmountFromSymbol(finalPaymentResult).amount

  useEffect(() => {
    dispatch(formFieldUpdated('payment', 1))
  }, [dispatch])

  // Register a no-op payment API for $0 amounts since no PaymentForm will be rendered
  useEffect(() => {
    if (finalPaymentAmount === 0 && registerApi) {
      registerApi({
        createPayment: () => Promise.resolve(true),
        handleCardAction: () => Promise.resolve(true)
      })
    }
  }, [finalPaymentAmount, registerApi])

  return (
    <EnterDetailsFieldStackNested>
      <Information>
        {i18n.t('booking.templates.payment.information')}
        {' *'}
      </Information>
      {hasActiveBookingCodes && isStripePayment && showCouponInput
        && (
        <Coupon
          inviteeEmail={inviteeEmail}
          onCouponApplied={() => {
            setShowCouponInput(false)
            setTimeout(() => {
              setPaymentElementKey((prev) => prev + 1)
            }, 100)
          }}
        />
        )}
      <EnterDetailsFieldset>
        {couponData && !showCouponInput
          ? (
            <InfoCoupon
              amountWithSymbol={amountWithSymbol}
              currency={currency}
              paymentTerms={paymentTerms}
              onRemoveCoupon={async () => {
                await validateCoupon({
                  eventTypeUuid,
                  clientSecret: stripeClientSecret.value
                })
                setShowCouponInput(true)
                setTimeout(() => {
                  setPaymentElementKey((prev) => prev + 1)
                }, 100)
              }}
            />
          ) : (
            <Info
              amountWithSymbol={amountWithSymbol}
              currency={currency}
              paymentTerms={paymentTerms}
            />
          )}
        {finalPaymentAmount > 0 && (
          <PaymentForm
            key={paymentElementKey}
            registerApi={registerApi}
            onPaypalAuthorized={onPaypalAuthorized}
          />
        )}
      </EnterDetailsFieldset>
    </EnterDetailsFieldStackNested>
  )
}

export default Payment
